import React, { PureComponent, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tableHelper } from '@builttocreate/engine-utils';

import { useVirtualizer } from '@tanstack/react-virtual';
import InputGroupFieldRow from './InputGroupFieldRow';
import handleTableFieldPageBreakAdjustments from '../../../../lib/tableFieldPageBreakManager';

import FieldModes from '../../../../constants/FieldModes';

import ThemeIcons from '../../../../theme/Icons';

import Tooltip from '../../../misc/Tooltip';
import Button from '../../../misc/Button';

const {
  generateTableLookup,
  generateTableLookupForRows,
  getRows,
  sortRows,
  getColumns,
  sortColumns,
  getCleanedRowOrder
} = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledInputGroup = styled.div`
  display: inline-block;
  overflow: auto;
  overflow: ${(props) => (props.fieldMode === FieldModes.edit ? 'hidden' : 'auto')};
  scrollbar-gutter: stable;
`;

const StyledVirtualizedInputGroup = styled(StyledInputGroup)`
  width: 100%;
  height: 100%;
`;

const StyledRowWrapper = styled.div`
  margin: 0px;
  padding-bottom: 8px;
`;

const StyledTooltipContainer = styled.div`
  position:absolute;
  right:0;
  top:0;
`;

class InputGroupField extends PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    disabled: PropTypes.bool,
    virtual: PropTypes.bool,
    fieldMode: PropTypes.string,
    pageBreakPointsPixels: PropTypes.array,

    value: PropTypes.array, //Rows
    rowOrder: PropTypes.array,

    tableColumns: PropTypes.array,
    tableColumnOrder: PropTypes.array,

    //SDK Styles
    primaryColor: PropTypes.string,
    dangerColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Column title styles
    columnTitleFontSize: PropTypes.number,
    columnTitleFontColor: PropTypes.string,
    columnTitleTextAlign: PropTypes.string,
    columnTitleFontStyle: PropTypes.string,
    columnTitleFontWeight: PropTypes.string,
    columnTitleTextTransform: PropTypes.string,
    columnTitleTextDecoration: PropTypes.string,
    columnTitleBackgroundColor: PropTypes.string,
    columnTitlePadding: PropTypes.number,

    //Cell styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    height: PropTypes.number,

    //Row Action Handlers
    onRowCreate: PropTypes.func,
    onRowDuplicate: PropTypes.func,
    onRowMoveUp: PropTypes.func,
    onRowMoveDown: PropTypes.func,
    onRowAddBelow: PropTypes.func,
    onRowDelete: PropTypes.func,

    //Cell Action Handlers
    onCellChange: PropTypes.func,

    //Used for calculating width and height of element
    measure: PropTypes.bool,
    onMeasureCompleted: PropTypes.func

  }

  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      columns: [],
      rowTableLookup: {},
      focused: '',

      //Used for measuring
      rowsLoadCompleted: false,
      measureCompleted: false,
    };
  }

  componentDidMount() {
    this.populateTableData();
  }

  componentDidUpdate(prevProps) {

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      value,
      rowOrder,
      tableColumns,
      tableColumnOrder,
      onMeasureCompleted
    } = this.props;

    const { measureCompleted, rowsLoadCompleted } = this.state;

    if (
      tableColumns !== prevProps.tableColumns
      || tableColumnOrder !== prevProps.tableColumnOrder
      || rowOrder !== prevProps.rowOrder
      || value !== prevProps.value //Rows
    ) {
      this.populateTableData();
    }

    if (onMeasureCompleted && !measureCompleted && rowsLoadCompleted && this.containerRef) {

      //Used to prevent onMeasureCompleted from being fired more than once.
      this.setState({ measureCompleted: true });

      onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, this.containerRef.getBoundingClientRect());

    }

  }

  populateTableData = () => {

    const { value, rowOrder, tableColumns, tableColumnOrder } = this.props;

    const formattedRows = this.getFormattedAndSortedRows(value, rowOrder);
    const formattedColumns = this.getFormattedAndSortedColumns(tableColumns, tableColumnOrder);

    const tableLookup = generateTableLookup(formattedRows, formattedColumns);
    const rowTableLookup = generateTableLookupForRows(formattedRows, formattedColumns, tableLookup);

    this.setState({
      rows: formattedRows,
      columns: formattedColumns,
      rowTableLookup,
    });

  }

  static defaultProps = {}

  render() {

    const { columns, rows, rowTableLookup, focused } = this.state;
    const {

      fieldPositionId,

      disabled,
      measure,
      virtual,
      fieldMode,
      pageBreakPointsPixels,
      height,
      title,
      description,
      tipTitle,
      tipDescription,
      tipVisible,

      primaryColor,
      dangerColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleFontStyle,
      titleFontWeight,
      titleTextAlign,
      titleTextTransform,
      titleTextDecoration,

      //Column title styles
      columnTitleFontSize,
      columnTitleFontColor,
      columnTitleFontStyle,
      columnTitleFontWeight,
      columnTitleTextAlign,
      columnTitleTextTransform,
      columnTitleTextDecoration,
      columnTitleBackgroundColor,
      columnTitlePadding,

      //Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,

    } = this.props;

    const InputGroupNode = virtual ? VirtualizedInputGroup : InputGroup;
    return (
      <StyledWrapper
        data-testid={`${fieldPositionId}-input-group-field`}
        measure={measure}
        ref={(e) => {
          this.containerRef = e;
        }}
      >
        {this.getTooltip()}
        <InputGroupNode
          id={fieldPositionId}
          fieldMode={fieldMode}
          title={title}
          description={description}
          tipTitle={tipTitle}
          tipDescription={tipDescription}
          tipVisible={tipVisible}
          disabled={disabled}
          focused={focused}
          columns={columns}
          rows={rows}
          rowTableLookup={rowTableLookup}
          pageBreakPointsPixels={pageBreakPointsPixels}
          {...{
            primaryColor,
            dangerColor,

            titleDisplay,
            titleFontSize,
            titleFontColor,
            titleFontStyle,
            titleFontWeight,
            titleTextAlign,
            titleTextTransform,
            titleTextDecoration,

            //Column title styles
            columnTitleFontSize,
            columnTitleFontColor,
            columnTitleFontStyle,
            columnTitleFontWeight,
            columnTitleTextAlign,
            columnTitleTextTransform,
            columnTitleTextDecoration,
            columnTitleBackgroundColor,
            columnTitlePadding,

            //Styles
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            height,
          }}
          onCellChange={this.handleCellChange}
          onCellFocus={this.handleCellFocus}
          onCellBlur={this.handleCellBlur}
          onCellUpload={this.handleCellUpload}
          onRowCreate={this.handleRowCreate}
          onRowAddBelow={this.handleRowAddBelow}
          onRowDuplicate={this.handleRowDuplicate}
          onRowMoveUp={this.handleRowMoveUp}
          onRowMoveDown={this.handleRowMoveDown}
          onRowDelete={this.handleRowDelete}
          onRowLoadCompleted={this.handleRowLoadCompleted}
        />
      </StyledWrapper>
    );

  }

  getTooltip = () => {

    const { tipVisible, tipTitle, tipDescription, fieldMode } = this.props;

    if (fieldMode === FieldModes.readonly) return null;

    return (
      <StyledTooltipContainer>
        <Tooltip
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipContainer>
    );
  }

  handleRowLoadCompleted = (rowIndex) => {

    const { rows, rowsLoadCompleted } = this.state;

    if (!this.loaded) this.loaded = {};
    this.loaded[rowIndex] = true;

    /**
     * Only update measurements once all fields have been measured.
     */
    if (!rowsLoadCompleted && Object.keys(this.loaded).length === rows.length) {
      this.setState({ rowsLoadCompleted: true });
    }

  }

  handleRowCreate = () => {

    const { onRowCreate, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const { rows } = this.state;

    if (onRowCreate) onRowCreate(fieldId, fieldIdentifier, fieldPositionId, rows.length - 1);

  }

  handleRowAddBelow = (rowId, rowIndex) => {

    const { onRowAddBelow, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onRowAddBelow) onRowAddBelow(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleRowDuplicate = (rowId, rowIndex) => {

    const { onRowDuplicate, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onRowDuplicate) onRowDuplicate(fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex);

  }

  handleRowMoveUp = (rowId, rowIndex) => {

    const { onRowMoveUp, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rowIndex === 0) return;

    if (onRowMoveUp) onRowMoveUp(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleRowMoveDown = (rowId, rowIndex) => {

    const { rows } = this.state;

    const { onRowMoveDown, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rows.length === 1 || (rows.length - 1) === rowIndex) return;

    if (onRowMoveDown) onRowMoveDown(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleRowDelete = (rowId, rowIndex) => {

    const { rows } = this.state;

    const { onRowDelete, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rows.length === 1) return;

    if (onRowDelete) onRowDelete(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleCellChange = (rowId, columnId, value) => {

    const { onCellChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onCellChange) onCellChange(fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, value);

  }

  handleCellUpload = (rowId, columnId, value) => {
    const { onCellUpload, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onCellUpload) onCellUpload(fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, value);
  }

  handleCellFocus = (rowId, columnId, columnIdentifier, e) => {

    const { onFocus, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onFocus) {

      const blurFunction = e && e.blur ? { blur: () => e.blur() } : {};

      onFocus(fieldId, fieldIdentifier, fieldPositionId, blurFunction, rowId, columnId, columnIdentifier);

    }

  }

  handleCellBlur = (rowId, columnId, columnIdentifier,) => {

    const { onBlur, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onBlur) {

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, columnId, columnIdentifier);

    }

  }

  getFormattedAndSortedColumns = (columns, columnOrder) => {
    return getColumns(sortColumns(columns, columnOrder));
  }

  getFormattedAndSortedRows = (rows, rowOrder) => {

    /**
    * getCleanedRowOrder returns a cleanedRowOrder after removing duplicate rowIds/ deletedRowIds.
    * If the rowOrder is passed as an empty array, we would generate a rowOrder based on
    * the rows that were passed via the input.
    */

    const cleanedRowOrder = getCleanedRowOrder(rowOrder, rows);

    return sortRows(getRows(rows), cleanedRowOrder);

  }

}

const VirtualizedInputGroup = ({

  id,
  title,
  description,
  tipTitle,
  tipDescription,
  tipVisible,
  disabled,
  focused,
  rows,
  columns,
  rowTableLookup,
  fieldMode,

  primaryColor,
  dangerColor,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleFontStyle,
  titleFontWeight,
  titleTextAlign,
  titleTextTransform,
  titleTextDecoration,

  //Column title styles
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,

  //Styles
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderWidth,
  borderRadius,
  backgroundColor,
  padding,

  //Actions
  onCellChange,
  onCellUpload,
  onCellFocus,
  onCellBlur,
  onRowCreate,
  onRowAddBelow,
  onRowDuplicate,
  onRowMoveUp,
  onRowMoveDown,
  onRowDelete,
  onRowLoadCompleted,

}) => {

  const parentRef = useRef();

  let totalRowLength = rows.length;
  if (!disabled) totalRowLength += 1; //Add +1 more for the footer (Duplicate Fields Button)

  const rowVirtualizer = useVirtualizer({
    count: totalRowLength,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
  });

  let rowWidth = 0;
  columns.forEach((column) => rowWidth += column.width);

  const handleRowAddBelow = useCallback((rowId, rowIndex) => {

    onRowAddBelow(rowId, rowIndex);

  }, [rows]);

  const handleRowMoveUp = useCallback((rowId, rowIndex) => {

    onRowMoveUp(rowId, rowIndex);

  }, [rows]);

  const handleRowMoveDown = useCallback((rowId, rowIndex) => {

    onRowMoveDown(rowId, rowIndex);

  }, [rows]);

  const handleDuplicateRow = useCallback((rowId, rowIndex) => {

    onRowDuplicate(rowId, rowIndex);

  }, [rows]);

  const handleRowDelete = useCallback((rowId, rowIndex) => {

    onRowDelete(rowId, rowIndex);

  }, [rows]);

  return (
    <>
      <StyledVirtualizedInputGroup data-testid={`${id}-input-group-field-table-virtual`} ref={parentRef}>
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {

            const rowIndex = virtualRow.index;
            const row = rows[rowIndex];

            const lastRow = virtualRow.index === (totalRowLength - 1);

            let displayRow = null;

            if (!disabled && lastRow) {
              displayRow = (
                <Button icon={ThemeIcons.misc.add} text="Add" onClick={onRowCreate} />
              );
            } else {
              displayRow = (
                <StyledRowWrapper>
                  <InputGroupFieldRow
                    id={id}
                    key={`${row._id}-${rowIndex}`}
                    title={title}
                    description={description}
                    tipTitle={tipTitle}
                    tipDescription={tipDescription}
                    tipVisible={tipVisible}
                    disabled={disabled}
                    focused={focused}
                    row={row}
                    rowIndex={rowIndex}
                    columns={columns}
                    tableLookup={rowTableLookup[rowIndex + 1] || {}}
                    fieldMode={fieldMode}
                    {...{

                      primaryColor,
                      dangerColor,

                      //Title styles
                      titleDisplay,
                      titleFontSize,
                      titleFontColor,
                      titleFontStyle,
                      titleFontWeight,
                      titleTextAlign,
                      titleTextTransform,
                      titleTextDecoration,

                      //Column title styles
                      columnTitleFontSize,
                      columnTitleFontColor,
                      columnTitleFontStyle,
                      columnTitleFontWeight,
                      columnTitleTextAlign,
                      columnTitleTextTransform,
                      columnTitleTextDecoration,
                      columnTitleBackgroundColor,
                      columnTitlePadding,

                      //Field styles
                      fontSize,
                      fontColor,
                      fontStyle,
                      fontWeight,
                      textAlign,
                      textTransform,
                      textDecoration,
                      textOverflow,
                      borderColor,
                      borderWidth,
                      borderRadius,
                      backgroundColor,
                      padding,
                    }}
                    onCellChange={onCellChange}
                    onCellFocus={onCellFocus}
                    onCellBlur={onCellBlur}
                    onCellUpload={onCellUpload}
                    onRowAddBelow={handleRowAddBelow}
                    onRowDuplicate={handleDuplicateRow}
                    onRowMoveUp={handleRowMoveUp}
                    onRowMoveDown={handleRowMoveDown}
                    onRowDelete={handleRowDelete}
                    onLoadCompleted={onRowLoadCompleted}
                  />
                </StyledRowWrapper>
              );
            }

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {displayRow}
              </div>
            );

          })}
        </div>
      </StyledVirtualizedInputGroup>
    </>
  );
};

const InputGroup = ({

  id,
  fieldMode,
  title,
  description,
  tipTitle,
  tipDescription,
  tipVisible,
  disabled,
  focused,
  rows,
  columns,
  rowTableLookup,
  pageBreakPointsPixels,

  primaryColor,
  dangerColor,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleFontStyle,
  titleFontWeight,
  titleTextAlign,
  titleTextTransform,
  titleTextDecoration,

  //Column title styles
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,

  //Styles
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderWidth,
  borderRadius,
  backgroundColor,
  padding,

  //Actions
  onCellChange,
  onCellUpload,
  onRowCreate,
  onRowAddBelow,
  onRowDuplicate,
  onRowMoveUp,
  onRowMoveDown,
  onRowDelete,
  onRowLoadCompleted

}) => {

  return (
    <>
      <StyledInputGroup
        data-testid={`${id}-input-group-field-table`}
        {...{ fieldMode }}
        ref={(ref) => handleTableFieldPageBreakAdjustments(ref, pageBreakPointsPixels, { borderWidth: 0 })}
      >
        {rows.map((row, rowIndex) => (
          <StyledRowWrapper key={`${row._id}-${rowIndex}`}>
            <InputGroupFieldRow
              id={id}
              title={title}
              description={description}
              tipTitle={tipTitle}
              tipDescription={tipDescription}
              tipVisible={tipVisible}
              key={`${row._id}-${rowIndex}`}
              disabled={disabled}
              focused={focused}
              row={row}
              rowIndex={rowIndex}
              columns={columns}
              tableLookup={rowTableLookup[rowIndex + 1] || {}}
              {...{
                primaryColor,
                dangerColor,

                //Title styles
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleFontStyle,
                titleFontWeight,
                titleTextAlign,
                titleTextTransform,
                titleTextDecoration,

                //Column title styles
                columnTitleFontSize,
                columnTitleFontColor,
                columnTitleFontStyle,
                columnTitleFontWeight,
                columnTitleTextAlign,
                columnTitleTextTransform,
                columnTitleTextDecoration,
                columnTitleBackgroundColor,
                columnTitlePadding,

                //Field styles
                fontSize,
                fontColor,
                fontStyle,
                fontWeight,
                textAlign,
                textTransform,
                textDecoration,
                textOverflow,
                borderColor,
                borderWidth,
                borderRadius,
                backgroundColor,
                padding,
              }}
              onCellChange={onCellChange}
              onCellUpload={onCellUpload}
              onRowAddBelow={onRowAddBelow}
              onRowDuplicate={onRowDuplicate}
              onRowMoveUp={onRowMoveUp}
              onRowMoveDown={onRowMoveDown}
              onRowDelete={onRowDelete}
              onLoadCompleted={onRowLoadCompleted}
            />
          </StyledRowWrapper>
        ))}
        {disabled ? null : <Button icon={ThemeIcons.misc.add} text="Add" onClick={onRowCreate} />}
      </StyledInputGroup>
    </>
  );
};

export default InputGroupField;
